import { useEffect, useState } from 'react'
import { request } from './StockAdmin'

/**
 * Automatický odpočet zásob z FiskalPRO – zapnutie/vypnutie, stav napojenia pokladníc na sklady
 * a ručné „Synchronizovať teraz“. Server navyše skúša synchronizovať sám každých 10 minút, ak je zapnuté.
 */

type Settings = { enabled: boolean; lastRunAt: string | null; lastSuccessAt: string | null; lastError: string | null; lastSummary: { sales: number; applied: number; skippedExisting: number; noWarehouse: number; matchedItems: number; unmatchedItems: number } | null }
type Mapping = { posId: number; posName: string; storeId: string; storeName: string; warehouseId: string | null; warehouseName: string | null; ready: boolean }

const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleString('sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'

export default function FiskalProStockSyncAdmin() {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [mappings, setMappings] = useState<Mapping[]>([])
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [runMessage, setRunMessage] = useState('')

  const load = () => request('/api/admin/fiskalpro-stock-sync').then(data => { setSettings(data.settings); setMappings(data.mappings) }).catch(e => setError(e instanceof Error ? e.message : 'Nepodarilo sa načítať.'))
  useEffect(() => { void load() }, [])

  async function toggle(enabled: boolean) {
    setSaving(true); setError('')
    try { const next = await request('/api/admin/fiskalpro-stock-sync', { method: 'PUT', body: JSON.stringify({ enabled }) }); setSettings(next) }
    catch (e) { setError(e instanceof Error ? e.message : 'Nepodarilo sa uložiť.') }
    finally { setSaving(false) }
  }

  async function runNow() {
    setRunning(true); setRunMessage(''); setError('')
    try {
      const { summary } = await request('/api/admin/fiskalpro-stock-sync/run', { method: 'POST' })
      setRunMessage(`Hotovo – ${summary.sales} predajov z pokladne, ${summary.applied} odpísaných na sklade${summary.unmatchedItems ? `, ${summary.unmatchedItems} položiek sa nenašlo v sklade` : ''}.`)
      await load()
    } catch (e) { setError(e instanceof Error ? e.message : 'Synchronizácia zlyhala.') }
    finally { setRunning(false) }
  }

  if (!settings) return <div className="panel fps-panel"><p className="empty-message">{error || 'Načítavam…'}</p></div>

  const notReady = mappings.filter(m => !m.ready)

  return <div className="panel fps-panel">
    <div className="pa-head">
      <div>
        <h2>Sklad ↔ FiskalPRO</h2>
        <p>Keď pokladňa FiskalPRO zaeviduje predaj, tovar sa automaticky odpíše zo skladu danej prevádzky. Párovanie produktov je podľa artiklu z FiskalPRO.</p>
      </div>
      <label className="fps-toggle"><input type="checkbox" checked={settings.enabled} disabled={saving} onChange={e => void toggle(e.target.checked)} /> Automatický odpočet zapnutý</label>
    </div>

    {mappings.length > 0 && <table className="fps-table"><thead><tr><th>Pokladňa FiskalPRO</th><th>Prevádzka</th><th>Sklad</th><th>Stav</th></tr></thead>
      <tbody>{mappings.map(m => <tr key={m.posId}>
        <td>{m.posName}</td><td>{m.storeName}</td><td>{m.warehouseName ?? '—'}</td>
        <td>{m.ready ? <span className="fps-ok">✓ napojené</span> : <span className="fps-warn">⚠ bez skladu so sledovaním stavu</span>}</td>
      </tr>)}</tbody>
    </table>}
    {!mappings.length && <p className="empty-message">Žiadna pokladňa nie je priradená prevádzke – nastavte to v časti „Správa prevádzok“.</p>}
    {notReady.length > 0 && <p className="fps-hint">Pre pokladne bez skladu sa predaje nebudú odpisovať – priraďte prevádzke sklad so sledovaním stavu zásob.</p>}

    <div className="fps-status">
      <div><span>Posledný beh</span><strong>{fmt(settings.lastRunAt)}</strong></div>
      <div><span>Posledná úspešná synchronizácia</span><strong>{fmt(settings.lastSuccessAt)}</strong></div>
      {settings.lastSummary && <div><span>Výsledok</span><strong>{settings.lastSummary.applied} odpísaných / {settings.lastSummary.sales} predajov</strong></div>}
    </div>
    {settings.lastError && <p className="error-message">Posledná chyba: {settings.lastError}</p>}
    {runMessage && <p className="fps-ok-message">{runMessage}</p>}
    {error && <p className="error-message">{error}</p>}

    <button type="button" className="pa-ghost" disabled={running} onClick={() => void runNow()}>{running ? 'Synchronizujem…' : '↻ Synchronizovať teraz'}</button>
    <p className="fps-note">Endpoint na čítanie predajov z FiskalPRO nie je verejne zdokumentovaný – po prvom spustení na ostrej pokladni môže byť potrebné doladenie na strane servera. Pri probléme pošlite chybovú hlášku vyššie.</p>
  </div>
}
